/**
 * TranslationsAdminPage — NLS translation resource manager (owner-admin only).
 * Route: /admin/translations
 */
import {
  AlertCircle,
  Copy,
  Download,
  Globe,
  History,
  Languages,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAuthentication } from '../../hooks/useAuthentication';
import { invalidateDbI18nCache } from '../../i18n';
import Layout from '../../modules/dashboard/components/Layout';
import type { DashboardPage } from '../../modules/dashboard/components/Layout';
import { isOwnerAdminEmail } from '../../modules/dashboard/components/Jobs/helpers';
import {
  buildI18nCsv,
  bulkCopyI18nLocale,
  deleteI18nResource,
  exportI18nResources,
  fetchI18nAuditLog,
  fetchI18nCoverage,
  importI18nFromCsv,
  I18N_LOCALE_CODES,
  parseEditString,
  previewValue,
  runAiTranslateBatch,
  searchI18nResources,
  upsertI18nResource,
  valueToEditString,
  type I18nAuditEntry,
  type I18nCoverageRow,
  type I18nLocaleCode,
  type I18nResourceRow,
  type I18nValueStatus,
} from '../../services/i18nAdminService';

const PAGE_SIZE = 50;
const LOCALE_LABELS: Record<I18nLocaleCode, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  ru: 'Русский',
};

interface TranslationsAdminPageProps {
  onSignOut?: () => void | Promise<void>;
  embedded?: boolean;
}

type EditDraft = Record<I18nLocaleCode, { text: string; status: I18nValueStatus }>;

function emptyEditDraft(): EditDraft {
  return {
    en: { text: '', status: 'published' },
    es: { text: '', status: 'published' },
    pt: { text: '', status: 'published' },
    ru: { text: '', status: 'published' },
  };
}

function draftFromRow(row: I18nResourceRow): EditDraft {
  const draft = emptyEditDraft();
  for (const code of I18N_LOCALE_CODES) {
    const entry = row.values_by_locale[code];
    draft[code] = {
      text: valueToEditString(entry?.value),
      status: entry?.status ?? 'published',
    };
  }
  return draft;
}

export default function TranslationsAdminPage({
  onSignOut = () => {},
  embedded = false,
}: TranslationsAdminPageProps) {
  const { session, user, loading: authLoading } = useAuthentication();
  const isAdmin = useMemo(
    () => isOwnerAdminEmail(user?.email ?? session?.user?.email),
    [user?.email, session?.user?.email],
  );

  const [coverage, setCoverage] = useState<I18nCoverageRow[]>([]);
  const [rows, setRows] = useState<I18nResourceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [missingLocale, setMissingLocale] = useState<I18nLocaleCode | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [editKey, setEditKey] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editDraft, setEditDraft] = useState<EditDraft>(emptyEditDraft);
  const [editIsNew, setEditIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const [bulkToLocale, setBulkToLocale] = useState<I18nLocaleCode>('es');
  const [bulkOnlyMissing, setBulkOnlyMissing] = useState(true);
  const [bulkRunning, setBulkRunning] = useState(false);

  const [aiToLocale, setAiToLocale] = useState<I18nLocaleCode>('es');
  const [aiOnlyMissing, setAiOnlyMissing] = useState(true);
  const [aiRunning, setAiRunning] = useState(false);

  const [exportRunning, setExportRunning] = useState(false);
  const [importRunning, setImportRunning] = useState(false);
  const importInputRef = React.useRef<HTMLInputElement>(null);

  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState<I18nAuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const handleNavigate = useCallback((pageId: DashboardPage) => {
    const pathMap: Partial<Record<DashboardPage, string>> = {
      'translations-admin': '/admin/translations',
      'account-settings': '/account-settings',
      dashboard: '/dashboard',
    };
    const path = pathMap[pageId] ?? '/dashboard';
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const loadCoverage = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await fetchI18nCoverage();
      setCoverage(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load coverage.');
    }
  }, [isAdmin]);

  const loadRows = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const { rows: data, total: count } = await searchI18nResources({
        search: debouncedSearch,
        missingLocale,
        limit: PAGE_SIZE,
        offset,
      });
      setRows(data);
      setTotal(count);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load translations.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, debouncedSearch, missingLocale]);

  useEffect(() => {
    void loadCoverage();
  }, [loadCoverage]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, missingLocale]);

  const openEdit = (row: I18nResourceRow) => {
    setEditIsNew(false);
    setEditKey(row.resource_key);
    setEditDescription(row.description ?? '');
    setEditDraft(draftFromRow(row));
  };

  const openCreate = () => {
    setEditIsNew(true);
    setEditKey('');
    setEditDescription('');
    setEditDraft(emptyEditDraft());
  };

  const closeEdit = () => {
    setEditKey(null);
    setEditIsNew(false);
    setSaving(false);
  };

  const handleSave = async () => {
    const key = editKey?.trim();
    if (!key) {
      setError('Resource key is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      for (const locale of I18N_LOCALE_CODES) {
        const { text, status } = editDraft[locale];
        if (!text.trim()) continue;
        const value = parseEditString(text);
        await upsertI18nResource({
          resourceKey: key,
          locale,
          value,
          status,
          description: editDescription.trim() || null,
        });
      }
      invalidateDbI18nCache();
      setToast(`Saved "${key}"`);
      closeEdit();
      await Promise.all([loadRows(), loadCoverage()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
      setSaving(false);
    }
  };

  const handleDelete = async (resourceKey: string) => {
    if (!window.confirm(`Delete "${resourceKey}" and all locale values?`)) return;
    setError(null);
    try {
      await deleteI18nResource(resourceKey);
      invalidateDbI18nCache();
      setToast(`Deleted "${resourceKey}"`);
      await Promise.all([loadRows(), loadCoverage()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    }
  };

  const handleBulkCopy = async () => {
    if (bulkToLocale === 'en') {
      setError('Bulk copy target cannot be English (use EN as source).');
      return;
    }
    setBulkRunning(true);
    setError(null);
    try {
      const count = await bulkCopyI18nLocale({
        fromLocale: 'en',
        toLocale: bulkToLocale,
        onlyMissing: bulkOnlyMissing,
        status: 'draft',
      });
      invalidateDbI18nCache();
      setToast(`Copied ${count} keys from EN → ${bulkToLocale.toUpperCase()} (${bulkOnlyMissing ? 'missing only' : 'all'})`);
      await Promise.all([loadRows(), loadCoverage()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bulk copy failed.');
    } finally {
      setBulkRunning(false);
    }
  };

  const handleAiTranslate = async () => {
    if (aiToLocale === 'en') {
      setError('AI translate target cannot be English.');
      return;
    }
    setAiRunning(true);
    setError(null);
    try {
      const result = await runAiTranslateBatch({
        toLocale: aiToLocale,
        fromLocale: 'en',
        onlyMissing: aiOnlyMissing,
        batchSize: 25,
        maxBatches: 8,
      });
      invalidateDbI18nCache();
      setToast(
        `AI translated ${result.translated} strings to ${aiToLocale.toUpperCase()} (${result.batches} Groq batches, status: needs_review)`,
      );
      await Promise.all([loadRows(), loadCoverage(), auditOpen ? loadAudit() : Promise.resolve()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'AI translate failed.');
    } finally {
      setAiRunning(false);
    }
  };

  const handleExportCsv = async () => {
    setExportRunning(true);
    setError(null);
    try {
      const data = await exportI18nResources();
      const csv = buildI18nCsv(data);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gograbjob-i18n-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setToast(`Exported ${data.length} keys to CSV`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setExportRunning(false);
    }
  };

  const handleImportCsv = async (file: File) => {
    setImportRunning(true);
    setError(null);
    try {
      const text = await file.text();
      const count = await importI18nFromCsv(text);
      invalidateDbI18nCache();
      setToast(`Imported ${count} locale values from CSV`);
      await Promise.all([loadRows(), loadCoverage()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed.');
    } finally {
      setImportRunning(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const data = await fetchI18nAuditLog(80, 0);
      setAuditRows(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load audit log.');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auditOpen) void loadAudit();
  }, [auditOpen, loadAudit]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const loadingSpinner = (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div
        className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
        style={{ borderColor: 'var(--ds-brand-border)', borderTopColor: 'var(--ds-brand)' }}
      />
    </div>
  );

  if (authLoading) {
    return embedded ? loadingSpinner : (
      <Layout currentPage="translations-admin" onNavigate={handleNavigate} onSignOut={onSignOut}>
        {loadingSpinner}
      </Layout>
    );
  }

  if (!isAdmin) {
    const denied = (
      <div className="ds-page px-4 py-12 max-w-lg mx-auto text-center space-y-3">
        <AlertCircle className="w-10 h-10 mx-auto" style={{ color: 'var(--ds-danger)' }} />
        <h1 className="t-h2">Admin access required</h1>
        <p className="t-meta">Translation management is restricted to the product owner account.</p>
      </div>
    );
    return embedded ? denied : (
      <Layout currentPage="translations-admin" onNavigate={handleNavigate} onSignOut={onSignOut}>
        {denied}
      </Layout>
    );
  }

  const editModal = (editKey !== null || editIsNew) && createPortal(
    <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div
        className="w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-xl vscode-bg-secondary border vscode-border shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="i18n-edit-title"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-4 py-3 border-b vscode-border vscode-bg-secondary">
          <h2 id="i18n-edit-title" className="t-h3">
            {editIsNew ? 'Add translation key' : 'Edit translation key'}
          </h2>
          <button type="button" onClick={closeEdit} className="ds-btn ds-btn-ghost ds-btn-sm min-h-[44px] min-w-[44px] p-2" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block t-label mb-1">Resource key</label>
            <input
              type="text"
              value={editKey ?? ''}
              onChange={(e) => setEditKey(e.target.value)}
              disabled={!editIsNew}
              placeholder="e.g. dashboard.overview.title"
              className="w-full min-h-[44px] px-3 rounded-lg border vscode-border vscode-bg-primary vscode-text-primary font-mono text-sm"
            />
          </div>
          <div>
            <label className="block t-label mb-1">Description (optional, for translators)</label>
            <input
              type="text"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className="w-full min-h-[44px] px-3 rounded-lg border vscode-border vscode-bg-primary vscode-text-primary text-sm"
            />
          </div>

          {I18N_LOCALE_CODES.map((locale) => (
            <div key={locale} className="rounded-lg border vscode-border p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="t-strong">{LOCALE_LABELS[locale]} ({locale})</span>
                <select
                  value={editDraft[locale].status}
                  onChange={(e) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      [locale]: { ...prev[locale], status: e.target.value as I18nValueStatus },
                    }))
                  }
                  className="min-h-[36px] px-2 rounded border vscode-border vscode-bg-primary text-sm"
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="needs_review">Needs review</option>
                </select>
              </div>
              <textarea
                value={editDraft[locale].text}
                onChange={(e) =>
                  setEditDraft((prev) => ({
                    ...prev,
                    [locale]: { ...prev[locale], text: e.target.value },
                  }))
                }
                rows={locale === 'en' ? 3 : 4}
                placeholder={locale === 'en' ? 'English source string' : 'Translated string (JSON for arrays/objects)'}
                className="w-full min-h-[80px] px-3 py-2 rounded-lg border vscode-border vscode-bg-primary vscode-text-primary text-sm font-mono"
              />
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 px-4 py-3 border-t vscode-border vscode-bg-secondary">
          <button type="button" onClick={closeEdit} className="ds-btn ds-btn-ghost min-h-[44px] touch-manipulation">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="ds-btn ds-btn-primary min-h-[44px] touch-manipulation"
          >
            {saving ? 'Saving…' : 'Save all locales'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );

  const content = (
    <div className="ds-page px-2 sm:px-4 lg:px-6 py-4 sm:py-6 max-w-[1600px] mx-auto w-full min-w-0 space-y-4">
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="ds-icon-square ds-icon-square-lg shrink-0">
            <Languages className="w-5 h-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <h1 className="t-h2">Translations (NLS)</h1>
            <p className="t-meta">{total.toLocaleString()} keys · database-backed UI strings</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void handleExportCsv()}
            disabled={exportRunning}
            className="ds-btn ds-btn-ghost min-h-[44px] touch-manipulation"
          >
            <Download className="w-4 h-4" />
            {exportRunning ? 'Exporting…' : 'Export CSV'}
          </button>
          <button
            type="button"
            onClick={() => importInputRef.current?.click()}
            disabled={importRunning}
            className="ds-btn ds-btn-ghost min-h-[44px] touch-manipulation"
          >
            <Upload className="w-4 h-4" />
            {importRunning ? 'Importing…' : 'Import CSV'}
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleImportCsv(file);
            }}
          />
          <button
            type="button"
            onClick={() => {
              invalidateDbI18nCache();
              setToast('Client translation cache cleared');
            }}
            className="ds-btn ds-btn-ghost min-h-[44px] touch-manipulation"
          >
            Clear app cache
          </button>
          <button type="button" onClick={openCreate} className="ds-btn ds-btn-primary min-h-[44px] touch-manipulation">
            <Plus className="w-4 h-4" />
            Add key
          </button>
        </div>
      </div>

      {coverage.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {coverage.map((c) => (
            <div key={c.locale} className="rounded-xl border vscode-border p-3 vscode-bg-tertiary">
              <div className="flex items-center gap-2 mb-1">
                <Globe className="w-4 h-4 t-brand" />
                <span className="t-strong uppercase">{c.locale}</span>
              </div>
              <p className="text-2xl font-bold vscode-text-primary">{c.coverage_pct}%</p>
              <p className="t-meta">
                {c.translated}/{c.total_keys} published · {c.missing} missing
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border vscode-border p-3 sm:p-4 space-y-3 vscode-bg-secondary">
        <p className="t-card-title">Bulk locale copy</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block t-label mb-1">From</label>
            <input value="EN (English)" readOnly className="min-h-[44px] px-3 rounded-lg border vscode-border vscode-bg-tertiary text-sm w-40" />
          </div>
          <div>
            <label className="block t-label mb-1">To</label>
            <select
              value={bulkToLocale}
              onChange={(e) => setBulkToLocale(e.target.value as I18nLocaleCode)}
              className="min-h-[44px] px-3 rounded-lg border vscode-border vscode-bg-primary text-sm w-40"
            >
              {I18N_LOCALE_CODES.filter((c) => c !== 'en').map((c) => (
                <option key={c} value={c}>{LOCALE_LABELS[c]}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 min-h-[44px] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={bulkOnlyMissing}
              onChange={(e) => setBulkOnlyMissing(e.target.checked)}
              className="w-4 h-4 accent-purple-600"
            />
            <span className="text-sm">Only keys missing in target locale</span>
          </label>
          <button
            type="button"
            onClick={() => void handleBulkCopy()}
            disabled={bulkRunning}
            className="ds-btn ds-btn-secondary min-h-[44px] touch-manipulation"
          >
            <Copy className="w-4 h-4" />
            {bulkRunning ? 'Copying…' : 'Copy EN → target (as draft)'}
          </button>
        </div>
      </div>

      <div className="rounded-xl border vscode-border p-3 sm:p-4 space-y-3 vscode-bg-secondary">
        <p className="t-card-title flex items-center gap-2">
          <Sparkles className="w-4 h-4 t-brand" />
          AI bulk translate (Groq)
        </p>
        <p className="t-meta text-sm">
          Translates English string keys via Groq. Results are saved as <code>needs_review</code> — publish after review.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block t-label mb-1">Target locale</label>
            <select
              value={aiToLocale}
              onChange={(e) => setAiToLocale(e.target.value as I18nLocaleCode)}
              className="min-h-[44px] px-3 rounded-lg border vscode-border vscode-bg-primary text-sm w-40"
            >
              {I18N_LOCALE_CODES.filter((c) => c !== 'en').map((c) => (
                <option key={c} value={c}>{LOCALE_LABELS[c]}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 min-h-[44px] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={aiOnlyMissing}
              onChange={(e) => setAiOnlyMissing(e.target.checked)}
              className="w-4 h-4 accent-purple-600"
            />
            <span className="text-sm">Only untranslated string keys</span>
          </label>
          <button
            type="button"
            onClick={() => void handleAiTranslate()}
            disabled={aiRunning}
            className="ds-btn ds-btn-primary min-h-[44px] touch-manipulation"
          >
            <Sparkles className="w-4 h-4" />
            {aiRunning ? 'Translating…' : 'Run AI translate'}
          </button>
        </div>
      </div>

      <div className="rounded-xl border vscode-border overflow-hidden">
        <button
          type="button"
          onClick={() => setAuditOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 vscode-bg-tertiary min-h-[44px] touch-manipulation"
        >
          <span className="t-card-title flex items-center gap-2">
            <History className="w-4 h-4" />
            Audit log
          </span>
          <span className="t-meta">{auditOpen ? 'Hide' : 'Show'}</span>
        </button>
        {auditOpen && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-t vscode-border vscode-bg-secondary text-left">
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Key</th>
                  <th className="px-3 py-2">Locale</th>
                </tr>
              </thead>
              <tbody>
                {auditLoading ? (
                  <tr><td colSpan={4} className="px-3 py-6 text-center t-meta">Loading…</td></tr>
                ) : auditRows.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-6 text-center t-meta">No audit entries yet.</td></tr>
                ) : (
                  auditRows.map((a) => (
                    <tr key={a.id} className="border-t vscode-border">
                      <td className="px-3 py-2 t-meta whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2">{a.action}</td>
                      <td className="px-3 py-2"><code className="text-xs">{a.resource_key}</code></td>
                      <td className="px-3 py-2">{a.locale ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div
        className="flex flex-wrap items-center gap-2 py-2"
        style={{ borderBottom: '1px solid var(--ds-divider)' }}
      >
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 vscode-text-tertiary pointer-events-none" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search resource keys…"
            className="w-full min-h-[44px] pl-10 pr-3 rounded-lg border vscode-border vscode-bg-primary text-sm"
          />
        </div>
        <select
          value={missingLocale}
          onChange={(e) => setMissingLocale(e.target.value as I18nLocaleCode | '')}
          className="min-h-[44px] px-3 rounded-lg border vscode-border vscode-bg-primary text-sm"
        >
          <option value="">All keys</option>
          {I18N_LOCALE_CODES.map((c) => (
            <option key={c} value={c}>Missing in {c.toUpperCase()}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void Promise.all([loadRows(), loadCoverage()])}
          className="ds-btn ds-btn-ghost min-h-[44px] min-w-[44px] p-2 touch-manipulation"
          aria-label="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border p-3 text-sm" style={{ borderColor: 'var(--ds-danger)', color: 'var(--ds-danger)' }}>
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {toast && (
        <div className="rounded-lg border px-3 py-2 text-sm t-strong" style={{ borderColor: 'var(--ds-brand-border)', background: 'var(--ds-brand-soft)' }}>
          {toast}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border vscode-border">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="vscode-bg-tertiary text-left">
              <th className="px-3 py-2 font-semibold min-w-[240px]">Resource key</th>
              {I18N_LOCALE_CODES.map((c) => (
                <th key={c} className="px-3 py-2 font-semibold min-w-[140px]">{c.toUpperCase()}</th>
              ))}
              <th className="px-3 py-2 font-semibold w-[100px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-12 text-center t-meta">Loading…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-12 text-center t-meta">No keys match your filters.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.resource_key} className="border-t vscode-border hover:vscode-bg-tertiary/50">
                  <td className="px-3 py-2 align-top">
                    <code className="text-xs break-all">{row.resource_key}</code>
                    {row.description && <p className="t-meta mt-1">{row.description}</p>}
                  </td>
                  {I18N_LOCALE_CODES.map((c) => {
                    const entry = row.values_by_locale[c];
                    const missing = entry == null || entry.value === undefined || entry.value === null;
                    return (
                      <td key={c} className="px-3 py-2 align-top">
                        {missing ? (
                          <span className="t-meta italic">—</span>
                        ) : (
                          <>
                            <span className="block text-xs">{previewValue(entry?.value)}</span>
                            {entry?.status && entry.status !== 'published' && (
                              <span className="ds-chip ds-chip-warn text-[10px] mt-1 inline-block">{entry.status}</span>
                            )}
                          </>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 align-top">
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="ds-btn ds-btn-ghost ds-btn-sm min-h-[36px] min-w-[36px] p-1"
                        aria-label="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(row.resource_key)}
                        className="ds-btn ds-btn-ghost ds-btn-sm min-h-[36px] min-w-[36px] p-1"
                        style={{ color: 'var(--ds-danger)' }}
                        aria-label="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="t-meta">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="ds-btn ds-btn-ghost min-h-[44px] touch-manipulation"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="ds-btn ds-btn-ghost min-h-[44px] touch-manipulation"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {editModal}
    </div>
  );

  if (embedded) return content;
  return (
    <Layout currentPage="translations-admin" onNavigate={handleNavigate} onSignOut={onSignOut}>
      {content}
    </Layout>
  );
}
